import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { runVoiceCommandChain } from "../../../utils/voiceCommands";
import { useSpeechRecognition } from "./useSpeechRecognition";
import { useAudioRecording } from "./useAudioRecording";
import { useSidebarTemplateSelection } from "./useSidebarTemplateSelection";
import { STT_MODE } from "./sidebarTemplateSelectionSession";
import { logSidebarTemplate, previewText } from "./sidebarTemplateSelectionDebug";
import {
  getSidebarDictationSttMode,
  hasSttText,
  insertRawAtCaret,
  isCaretAtEnd,
  renderSonioxText,
} from "../sidebarSttDisplay";

/**
 * Sidebar dictation: socket STT, shared voice-command engine, Soniox display rules.
 */
export function useSidebarDictation({
  sidebarTranscript,
  setSidebarTranscript,
  transcriptTextareaRef,
  dictationLanguage,
  anathomy,
  macros,
  templates,
  macroCommandMode,
  setMacroCommandMode,
  onBeforeStartRecording,
  onSelectedTemplateIdChange,
  onTemplateSelect,
  onVoiceMacroResult,
  notifyCreMirrorSuccess,
  notifyCreMirrorWarning,
  t,
}) {
  const isRecordingRef = useRef(false);
  const macroCommandModeRef = useRef(false);
  const handleSidebarSttEventRef = useRef(null);
  const sidebarVoiceAbortRef = useRef(false);
  const preRecordingSidebarRef = useRef("");
  const sttCumulativeRef = useRef("");
  const userEditedDuringSessionRef = useRef(false);
  const suppressSttEditDetectionRef = useRef(false);

  const {
    resetForMicSession: resetTemplateSelectionSession,
    onTranscriptCleared: onTemplateSelectionTranscriptCleared,
    updateSidebarText: updateTemplateSelectionSidebarText,
    ingestRawCumulative: ingestTemplateSelectionCumulative,
    getVoiceTemplateGate,
    getRawCumulative: getTemplateSelectionRawCumulative,
    finalizeAfterTemplateAttempt,
    lockAfterTemplateSelected,
  } = useSidebarTemplateSelection();

  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const applySidebarTemplateSelection = useCallback(
    (selectedTemplate) => {
      logSidebarTemplate("template-applied", {
        templateId: selectedTemplate?.templateId,
        templateName: selectedTemplate?.templateName,
        sidebarPreview: previewText(
          transcriptTextareaRef.current?.value ?? sidebarTranscript ?? "",
        ),
      });
      lockAfterTemplateSelected();
      onSelectedTemplateIdChange(selectedTemplate.templateId);
      onTemplateSelect(selectedTemplate);
      toast.success(
        t("templateSelected", {
          templateName: selectedTemplate.templateName ?? "",
        }),
      );
      notifyCreMirrorSuccess(
        t("templateSelected", {
          templateName: selectedTemplate.templateName ?? "",
        }),
      );
    },
    [
      onSelectedTemplateIdChange,
      onTemplateSelect,
      t,
      notifyCreMirrorSuccess,
      lockAfterTemplateSelected,
      transcriptTextareaRef,
      sidebarTranscript,
    ],
  );

  const insertAtCaretSpaced = useCallback(
    (chunk) => {
      const text = String(chunk ?? "").trim();
      if (!text) return;
      const textarea = transcriptTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const currentValue = textarea.value;
        const transcriptToAdd =
          currentValue && start > 0 ? ` ${text}` : text;
        const nextValue =
          currentValue.substring(0, start) +
          transcriptToAdd +
          currentValue.substring(end);
        suppressSttEditDetectionRef.current = true;
        setSidebarTranscript(nextValue);
        setTimeout(() => {
          if (!transcriptTextareaRef.current) return;
          const newCursorPos = start + transcriptToAdd.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          textarea.focus();
          if (newCursorPos >= nextValue.length) {
            textarea.scrollTop = textarea.scrollHeight;
          }
        }, 0);
      } else {
        setSidebarTranscript((prev) => (prev ? `${prev} ${text}` : text));
      }
    },
    [setSidebarTranscript, transcriptTextareaRef],
  );

  const insertSidebarDictationRaw = useCallback(
    (rawDelta) => {
      if (!hasSttText(rawDelta)) return;
      const ta = transcriptTextareaRef.current;
      if (!ta) {
        suppressSttEditDetectionRef.current = true;
        setSidebarTranscript((prev) => prev + String(rawDelta));
        return;
      }
      const { text, caret } = insertRawAtCaret(
        ta.value,
        rawDelta,
        ta.selectionStart,
        ta.selectionEnd,
      );
      suppressSttEditDetectionRef.current = true;
      setSidebarTranscript(text);
      requestAnimationFrame(() => {
        try {
          ta.setSelectionRange(caret, caret);
        } catch {
          /* ignore */
        }
      });
    },
    [setSidebarTranscript, transcriptTextareaRef],
  );

  const syncSidebarFromSoniox = useCallback(
    (finalsCumulative, interimText = "") => {
      const next = renderSonioxText(
        preRecordingSidebarRef.current,
        finalsCumulative,
        interimText,
      );
      suppressSttEditDetectionRef.current = true;
      setSidebarTranscript(next);
      requestAnimationFrame(() => {
        const ta = transcriptTextareaRef.current;
        if (!ta) return;
        const end = next.length;
        try {
          ta.setSelectionRange(end, end);
        } catch {
          /* ignore */
        }
        ta.scrollTop = ta.scrollHeight;
      });
    },
    [setSidebarTranscript, transcriptTextareaRef],
  );

  const handleSidebarTranscriptChange = useCallback(
    (value) => {
      setSidebarTranscript(value);

      if (!hasSttText(value)) {
        onTemplateSelectionTranscriptCleared();
        preRecordingSidebarRef.current = "";
        sttCumulativeRef.current = "";
        userEditedDuringSessionRef.current = true;
      } else {
        updateTemplateSelectionSidebarText(value);
      }

      if (suppressSttEditDetectionRef.current) {
        suppressSttEditDetectionRef.current = false;
        return;
      }
      if (!isRecordingRef.current || macroCommandModeRef.current) return;
      const autoText = renderSonioxText(
        preRecordingSidebarRef.current,
        sttCumulativeRef.current,
        "",
      );
      if (value !== autoText) {
        userEditedDuringSessionRef.current = true;
      }
    },
    [
      setSidebarTranscript,
      onTemplateSelectionTranscriptCleared,
      updateTemplateSelectionSidebarText,
    ],
  );

  const getSidebarText = useCallback(() => {
    const fromTextarea = transcriptTextareaRef.current?.value;
    if (fromTextarea != null) return fromTextarea;
    return String(sidebarTranscript ?? "");
  }, [transcriptTextareaRef, sidebarTranscript]);

  const applyVoiceCommandRemainder = useCallback(
    (remainder) => {
      if (!hasSttText(remainder)) return;
      if (macroCommandModeRef.current) {
        insertAtCaretSpaced(remainder);
      } else {
        insertSidebarDictationRaw(remainder);
      }
    },
    [insertAtCaretSpaced, insertSidebarDictationRaw],
  );

  const runSidebarVoiceEngine = useCallback(
    (
      segment,
      { allowTemplateCommands = true, allowTriggerlessTemplate = true } = {},
    ) => {
      sidebarVoiceAbortRef.current = false;
      const result = runVoiceCommandChain(segment, {
        language: dictationLanguage,
        handlers: {},
        // Transcription (right): template selection only — no macros/static editor cmds.
        allowStaticCommands: false,
        allowMacroCommands: false,
        allowTemplateCommands,
        allowTriggerlessTemplate:
          allowTemplateCommands && allowTriggerlessTemplate,
        templates,
        onVoiceTemplateResult: (templateResult) => {
          if (templateResult?.success && templateResult.template) {
            applySidebarTemplateSelection(templateResult.template);
            return;
          }
          const name = templateResult?.templateName ?? "";
          const warnMsg = t("voiceTemplateNotFound", { templateName: name });
          toast.warning(warnMsg);
          notifyCreMirrorWarning(warnMsg);
          sidebarVoiceAbortRef.current = true;
        },
      });
      if (sidebarVoiceAbortRef.current) {
        return { executedAny: true, remainder: "", abort: true };
      }
      return { ...result, abort: false };
    },
    [
      dictationLanguage,
      templates,
      applySidebarTemplateSelection,
      t,
      notifyCreMirrorWarning,
    ],
  );

  const handleStreamExpiredRef = useRef(() => {});

  const {
    streamingTranscript,
    setStreamingTranscript,
    connectSocket,
    disconnectSocket,
    socketRef: speechSocketRef,
  } = useSpeechRecognition({
    onFinalSegment: (payload) => {
      handleSidebarSttEventRef.current?.(payload);
    },
    onStreamExpired: () => handleStreamExpiredRef.current(),
  });

  const handleSidebarSttEvent = useCallback(
    (
      { transcript, transcriptCumulative } = {},
      { ignoreRecording = false, flushInterim = false } = {},
    ) => {
      if (!ignoreRecording && !isRecordingRef.current) return;

      const delta = transcript != null ? String(transcript) : "";
      const rawCumulative =
        transcriptCumulative != null ? String(transcriptCumulative) : "";
      const commandMode = macroCommandModeRef.current;

      if (!ignoreRecording) {
        setStreamingTranscript("");
      }

      const sttMode = getSidebarDictationSttMode(dictationLanguage);
      const isSonioxMode = sttMode === STT_MODE.SONIOX;
      const { sessionText } = ingestTemplateSelectionCumulative(rawCumulative);
      sttCumulativeRef.current = sessionText;

      const sidebarTextNow = getSidebarText();
      updateTemplateSelectionSidebarText(sidebarTextNow);

      const templateGate = getVoiceTemplateGate({
        commandMode,
        sidebarText: sidebarTextNow,
        sessionText,
        pendingDelta: delta,
      });

      const templateMatchSegment =
        sttMode === STT_MODE.SONIOX
          ? templateGate.allowTriggerlessTemplate && templateGate.templateMatchText
            ? templateGate.templateMatchText
            : templateGate.allowTriggerlessTemplate && hasSttText(delta)
              ? delta
              : ""
          : templateGate.allowTriggerlessTemplate && templateGate.templateMatchText
            ? templateGate.templateMatchText
            : delta;

      logSidebarTemplate("stt-event", {
        dictationLanguage,
        sttMode,
        commandMode,
        ignoreRecording,
        flushInterim,
        crossingThreshold: templateGate.crossingThreshold,
        sessionWordCount: templateGate.sessionWordCount,
        previousSessionWordCount: templateGate.previousSessionWordCount,
        deltaLength: delta.length,
        rawCumulativeLength: rawCumulative.length,
        templateMatchSegmentPreview: previewText(templateMatchSegment),
        userEditedDuringSession: userEditedDuringSessionRef.current,
      });

      let voice = runSidebarVoiceEngine(
        hasSttText(templateMatchSegment) ? templateMatchSegment : delta,
        {
          allowTemplateCommands: templateGate.allowTemplateCommands,
          allowTriggerlessTemplate: templateGate.allowTriggerlessTemplate,
        },
      );

      finalizeAfterTemplateAttempt();

      if (voice.executedAny || voice.abort) {
        logSidebarTemplate("voice-engine-result", {
          executedAny: voice.executedAny,
          abort: voice.abort,
          remainderPreview: previewText(voice.remainder),
        });
      }

      if (voice.abort) {
        if (!ignoreRecording) setStreamingTranscript("");
        return;
      }

      if (commandMode && !voice.executedAny) {
        applyVoiceCommandRemainder(voice.remainder);
        if (!ignoreRecording) setStreamingTranscript("");
        return;
      }

      if (flushInterim) {
        const interim = delta;
        if (
          isSonioxMode &&
          !userEditedDuringSessionRef.current &&
          isCaretAtEnd(transcriptTextareaRef.current)
        ) {
          syncSidebarFromSoniox(sttCumulativeRef.current, interim);
        } else if (hasSttText(interim)) {
          insertSidebarDictationRaw(interim);
        }
        if (!ignoreRecording) setStreamingTranscript("");
        return;
      }

      if (
        isSonioxMode &&
        !userEditedDuringSessionRef.current &&
        rawCumulative &&
        isCaretAtEnd(transcriptTextareaRef.current)
      ) {
        syncSidebarFromSoniox(sttCumulativeRef.current, "");
        return;
      }

      if (hasSttText(delta)) {
        insertSidebarDictationRaw(delta);
      } else if (hasSttText(voice.remainder) && !rawCumulative) {
        applyVoiceCommandRemainder(voice.remainder);
      }
    },
    [
      runSidebarVoiceEngine,
      applyVoiceCommandRemainder,
      syncSidebarFromSoniox,
      insertSidebarDictationRaw,
      setStreamingTranscript,
      dictationLanguage,
      getSidebarText,
      ingestTemplateSelectionCumulative,
      updateTemplateSelectionSidebarText,
      getVoiceTemplateGate,
      finalizeAfterTemplateAttempt,
      transcriptTextareaRef,
    ],
  );

  useEffect(() => {
    handleSidebarSttEventRef.current = handleSidebarSttEvent;
  }, [handleSidebarSttEvent]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const resetSttSession = useCallback(() => {
    setStreamingTranscript("");
    preRecordingSidebarRef.current =
      transcriptTextareaRef.current?.value ?? "";
    sttCumulativeRef.current = "";
    userEditedDuringSessionRef.current = false;
    suppressSttEditDetectionRef.current = false;
    resetTemplateSelectionSession({
      sttMode: getSidebarDictationSttMode(dictationLanguage),
    });
  }, [
    setStreamingTranscript,
    transcriptTextareaRef,
    resetTemplateSelectionSession,
    dictationLanguage,
  ]);

  const flushSttOnMicStop = useCallback(() => {
    if (
      macroCommandModeRef.current ||
      hasSttText(streamingTranscript) ||
      hasSttText(getTemplateSelectionRawCumulative())
    ) {
      handleSidebarSttEvent(
        {
          transcript: streamingTranscript,
          transcriptCumulative: getTemplateSelectionRawCumulative(),
        },
        {
          ignoreRecording: true,
          flushInterim: !macroCommandModeRef.current,
        },
      );
    } else {
      setStreamingTranscript("");
    }
  }, [
    streamingTranscript,
    handleSidebarSttEvent,
    setStreamingTranscript,
    getTemplateSelectionRawCumulative,
  ]);

  const startRecordingTimer = useCallback(() => {
    setRecordingTime(0);
    startTimeRef.current = Date.now();
    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setRecordingTime(elapsed);
    };
    timerRef.current = setInterval(updateTimer, 1000);
    updateTimer();
  }, []);

  const stopRecordingTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const {
    isRecording,
    stopRecording: stopAudioRecording,
    toggleRecording: toggleAudioRecording,
    socketRef: audioSocketRef,
  } = useAudioRecording({
    connectSocket,
    disconnectSocket,
    language: dictationLanguage,
    anathomy,
    logPrefix: "sidebar-mic",
    onBeforeStart: onBeforeStartRecording,
    onSessionStart: () => {
      resetSttSession();
      startRecordingTimer();
      isRecordingRef.current = true;
    },
    onSessionEnd: () => {
      flushSttOnMicStop();
      stopRecordingTimer();
      isRecordingRef.current = false;
    },
    onError: (error) => {
      console.error("Sidebar recording error:", error);
      stopRecordingTimer();
      isRecordingRef.current = false;
    },
  });

  useEffect(() => {
    handleStreamExpiredRef.current = () => {
      if (isRecordingRef.current) {
        stopAudioRecording();
      }
    };
  }, [stopAudioRecording]);

  useEffect(() => {
    macroCommandModeRef.current = macroCommandMode;
  }, [macroCommandMode]);

  const handleDictationLanguageChange = useCallback(
    (selectedLanguage) => {
      const socket = audioSocketRef.current ?? speechSocketRef.current;
      if (socket?.connected) {
        socket.emit("setLanguage", { language: selectedLanguage });
      }
    },
    [audioSocketRef, speechSocketRef],
  );

  const handleCommandModeButtonClick = useCallback(async () => {
    if (macroCommandMode) {
      setMacroCommandMode(false);
      return;
    }
    setMacroCommandMode(true);
    if (!isRecording) {
      const started = await toggleAudioRecording();
      if (!started) {
        setMacroCommandMode(false);
      }
    }
  }, [macroCommandMode, isRecording, setMacroCommandMode, toggleAudioRecording]);

  const handleSidebarMicPopoverStop = useCallback(async () => {
    if (isRecording) {
      await toggleAudioRecording();
    }
    if (macroCommandMode) {
      setMacroCommandMode(false);
    }
  }, [isRecording, macroCommandMode, setMacroCommandMode, toggleAudioRecording]);

  const resetRecordingTimerDisplay = useCallback(() => {
    stopRecordingTimer();
    setRecordingTime(0);
  }, [stopRecordingTimer]);

  const clearSidebarReportState = useCallback(() => {
    handleSidebarTranscriptChange("");
    setStreamingTranscript("");
    if (!isRecordingRef.current) {
      resetRecordingTimerDisplay();
    }
  }, [
    handleSidebarTranscriptChange,
    setStreamingTranscript,
    resetRecordingTimerDisplay,
  ]);

  return {
    streamingTranscript,
    isRecording,
    recordingTime,
    handleSidebarTranscriptChange,
    handleDictationLanguageChange,
    handleCommandModeButtonClick,
    handleSidebarMicPopoverStop,
    toggleRecording: toggleAudioRecording,
    clearStreamingTranscript: () => setStreamingTranscript(""),
    resetRecordingTimerDisplay,
    clearSidebarReportState,
  };
}
